import { UserPlus, Check } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";
import { authClient } from "@/lib/auth-client";
import { useTeam, useTeamMember } from "@/hooks/queries/useTeam";
import { useAssignContracting } from "@/hooks/mutations/useTeamMutations";
import type { TeamMemberRef } from "@/types/domain";

/** Iniciales para el avatar sin foto. */
function initials(name: string) {
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("");
}

export function MemberAvatar({
  member,
  className,
}: {
  member: TeamMemberRef;
  className?: string;
}) {
  return (
    <Avatar className={cn("size-6", className)}>
      {member.image ? <AvatarImage src={member.image} alt={member.name} /> : null}
      <AvatarFallback className="bg-primary/10 text-[10px] font-semibold text-primary">
        {initials(member.name)}
      </AvatarFallback>
    </Avatar>
  );
}

/**
 * Quién lleva una licitación, y el menú para traspasarla. En cuentas individuales
 * no renderiza nada: no hay a quién asignarle.
 *
 * `compact` deja solo el avatar, para la tarjeta del kanban y la fila de la tabla.
 */
export function AssigneeSelect({
  contractingId,
  assignedUserId,
  compact = false,
}: {
  contractingId: number;
  assignedUserId?: string | null;
  compact?: boolean;
}) {
  const { data: team } = useTeam();
  const { data: session } = authClient.useSession();
  const myUserId = session?.user.id;
  const assignedUser = useTeamMember(assignedUserId);
  const assign = useAssignContracting();

  if (!team?.isTeam) return null;

  const activeMembers = team.members.filter((member) => member.isActive);
  // Tomarla uno mismo lo puede cualquiera; ponerle una a otro necesita `canAssign`,
  // que es lo que el backend valida. Mostrar el menú completo sin el permiso solo
  // sirve para que el clic termine en 403.
  const canAssignOthers = !!team.canAssign;
  const selectableMembers = canAssignOthers
    ? activeMembers
    : activeMembers.filter((member) => member.userId === myUserId);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        onClick={(event) => event.stopPropagation()}
        className={cn(
          "flex items-center gap-2 rounded-full text-xs font-medium transition-colors",
          "focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none",
          compact
            ? "p-0.5 hover:bg-slate-100 dark:hover:bg-slate-800"
            : "border border-slate-200 px-2 py-1 text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800",
        )}
        title={
          assignedUser ? `La lleva ${assignedUser.name}` : "Sin responsable"
        }
      >
        {assignedUser ? (
          <>
            <MemberAvatar member={assignedUser} />
            {!compact && <span className="truncate">{assignedUser.name}</span>}
          </>
        ) : (
          <>
            <span
              className={cn(
                "flex items-center justify-center rounded-full border border-dashed border-slate-300 text-slate-400 dark:border-slate-600",
                compact ? "size-6" : "size-5",
              )}
            >
              <UserPlus className="size-3" />
            </span>
            {!compact && <span>Asignar</span>}
          </>
        )}
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align="end"
        onClick={(event) => event.stopPropagation()}
      >
        {selectableMembers.map((member) => {
          const isAssigned = member.userId === assignedUser?.id;
          return (
            <DropdownMenuItem
              key={member.userId}
              onSelect={() =>
                assign.mutate({
                  contracting_id: contractingId,
                  assigned_user_id: isAssigned ? null : member.userId,
                })
              }
              className="gap-2"
            >
              <MemberAvatar
                member={{
                  id: member.userId,
                  name: member.name,
                  image: member.image,
                }}
              />
              <span className="flex-1 truncate">{member.name}</span>
              {isAssigned && <Check className="size-3.5 text-primary" />}
            </DropdownMenuItem>
          );
        })}

        {selectableMembers.length === 0 && (
          <DropdownMenuItem disabled className="text-xs">
            Solo quien lleva la cuenta puede traspasar.
          </DropdownMenuItem>
        )}

        {assignedUser && (canAssignOthers || assignedUser.id === myUserId) && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onSelect={() =>
                assign.mutate({
                  contracting_id: contractingId,
                  assigned_user_id: null,
                })
              }
              className="text-slate-500"
            >
              Quitar responsable
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
